import { fg } from '@atlaskit/platform-feature-flags';

import type {
	SelectFilterDeps,
	SelectFilterOption,
} from '../../../common/constants/filters/select-filter/types';

import { getProductForFilterOption } from './get-product-for-filter-option';

/** Returns the available options for a select filter, optionally filtered by lookup input. */
export const getOptions: <ID extends string>({
	initial,
	state,
	config,
}: SelectFilterDeps<ID>) => (options: { isLookupActive: boolean }) => {
	isSelected: boolean;
	trackingKey: string;
	value: string;
	queryParamValue: string;
	isSuggested: boolean;
	isDefault: boolean;
	avatarUrl?: string;
	label: string;
	metadata?: import('../../../common/constants/filters/select-filter/types').SelectFilterOptionMetadata;
	product?: string;
}[] = <ID extends string>({ initial, state, config }: SelectFilterDeps<ID>) => {
	return (options: { isLookupActive: boolean }) => {
		const productBasedOptionsFilter = fg('product_based_filter_options')
			? (option: SelectFilterOption) => {
					return (
						option.product === getProductForFilterOption({ config, isUniversal: initial.universal })
					);
				}
			: () => true;
		const allSelectedValues = new Set([
			...state.queryParams.options
				.filter(productBasedOptionsFilter)
				.filter((option) => option.isSelected)
				.map((option) => option.value),
			...state.initialOptions.options
				.filter(productBasedOptionsFilter)
				.filter((option) => option.isSelected)
				.map((option) => option.value),
			...state.lookup.options
				.filter(productBasedOptionsFilter)
				.filter((lookupOption) => lookupOption.isSelected)
				.map((option) => option.value),
		]);

		if (options.isLookupActive) {
			const lookupResults = state.lookup.lookupResultOptions
				.map(
					(option) =>
						({
							...option,
							isSelected: allSelectedValues.has(option.value),
						}) satisfies SelectFilterOption,
				)
				.sort((option1, option2) => Number(option2.isSelected) - Number(option1.isSelected));

			return lookupResults;
		}

		const duplicateOptions = new Set();

		const newOptions = [
			...state.queryParams.options, // Query Params first
			// 'interacted' lookup options are options that a user made a lookup for and has either
			// selected or deselected an option from a lookup query
			// The 'interacted' options are persisted in  state.lookup.options
			...state.lookup.options,
			...state.initialOptions.options,
		]
			.filter(productBasedOptionsFilter)
			.filter(
				(option) => !duplicateOptions.has(option.value) && duplicateOptions.add(option.value), // remove duplicates
			)
			.map((option) => ({
				...option,
				isSelected: allSelectedValues.has(option.value),
			}));

		if (initial.sortCompareFn) {
			return newOptions.sort(initial.sortCompareFn);
		}

		return newOptions;
	};
};
